import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AdmittedPatientsModal from "../admission/AdmittedPatientsModal";
import CommonButton from "../../common11/Buttons/CommonButton";
import CommonModal from "../../common11/commonModals/CommonModal";
import CommonCheckbox from "../../common11/formInputs/CommonCheckbox";
import CommonDatePicker from "../../common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../../common11/formInputs/CommonTimePicker";
import InputAreaField from "../../common11/formInputs/InputAreaField";
import InputField from "../../common11/formInputs/InputField";
import SearchDropdown from "../../common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../common11/toastUtils";
import { IpdPatientInfoCard } from "../shared/IpdPatientSearchPanel";
import { getDefaultDateTime } from "../shared/ipdPatientUtils";

import {
  listAdmittedPatientsForDoctorRound,
  mapFormToSavePayload,
  mapListRowForTable,
  mapRecordToFormValues,
  mapWebPatientToAdmissionRow,
} from "../../../services/intakeOutputService";
import {
  clearEditing,
  fetchIntakeOutputById,
  fetchIntakeOutputList,
  saveIntakeOutput,
  setPatient,
  upsertDoctor,
} from "../../../store/slices/intakeOutputSlice";

const INTAKE_FIELDS = [
  { name: "oral", label: "Oral" },
  { name: "ng", label: "NG" },
  { name: "intakeOthers", label: "Others" },
  { name: "iv", label: "IV" },
  { name: "totalParenteral", label: "Total Parenteral" },
  { name: "intakeTotal", label: "Total" },
];

const OUTPUT_FIELDS = [
  { name: "urine", label: "Urine" },
  { name: "drain", label: "Drain" },
  { name: "aspiration", label: "Aspiration" },
  { name: "outputOthers", label: "Others" },
  { name: "outputTotal", label: "Total" },
];

const LIST_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "doctor", label: "Doctor" },
  { key: "intakeTotal", label: "Intake Total" },
  { key: "outputTotal", label: "Output Total" },
  { key: "addedBy", label: "Added By" },
];

const sameId = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

const toDoctorOption = (doctorId, doctorName) => {
  const id = Number(doctorId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    label: doctorName || `Doctor #${id}`,
    value: id,
  };
};

const mapAdmissionRowToPatient = (row) => ({
  uhid: row.uhid || "",
  patientName: row.patientName || "",
  contact: row.contact || "",
  patientGender: row.gender || "",
  patientAge: row.age || "",
  doctorName: row.doctorName || "",
  doctorId: row.doctorId != null ? Number(row.doctorId) : null,
  wardName: row.wardName || "",
  bedNo: row.bedNo || "",
  admissionId: Number(row.admissionId) || 0,
  regId: row.regId != null ? Number(row.regId) : null,
});

function IntakeOutputChartScreen() {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const { patient, doctors, records, editingInOutId, loading, saving } =
    useSelector((state) => state.intakeOutput);

  const { date: defaultDate, time: defaultTime } = getDefaultDateTime();
  const [listModalOpen, setListModalOpen] = useState(false);
  const [patientsModalOpen, setPatientsModalOpen] = useState(false);
  const [admissionRows, setAdmissionRows] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const defaultValues = {
    patientSearch: null,
    date: defaultDate,
    time: defaultTime,
    doctor: null,
    addedBy: "",
    intakeEnabled: true,
    outputEnabled: true,
    oral: "0",
    ng: "0",
    intakeOthers: "0",
    iv: "0",
    totalParenteral: "0",
    intakeTotal: "0",
    urine: "0",
    drain: "0",
    aspiration: "0",
    outputOthers: "0",
    outputTotal: "0",
    remark: "",
  };

  const { control, handleSubmit, reset, setValue } = useForm({ defaultValues });
  const formBusy = loading || saving;

  const doctorOptions = useMemo(
    () =>
      (doctors || []).map((d) => ({
        id: d.doctorId,
        label: d.doctorName,
        value: d.doctorId,
      })),
    [doctors]
  );

  const tableRows = useMemo(
    () => (records || []).map(mapListRowForTable),
    [records]
  );

  const loadIpPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const data = await listAdmittedPatientsForDoctorRound({
        isDischarged: false,
      });
      setAdmissionRows((data || []).map(mapWebPatientToAdmissionRow));
    } catch (error) {
      setAdmissionRows([]);
      showErrorToast(error.message || "Failed to load admitted patients");
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIpPatients();
  }, [loadIpPatients]);

  useEffect(() => {
    if (authUser?.userName) {
      setValue("addedBy", authUser.userName);
    }
  }, [authUser?.userName, setValue]);

  const patientSearchOptions = useMemo(
    () =>
      admissionRows
        .filter((row) => row.status === "Admitted")
        .map((row) => ({
          id: row.admissionId,
          label: [
            row.patientName,
            row.uhid,
            row.age ? `${row.age}Y` : null,
            row.doctorName,
          ]
            .filter(Boolean)
            .join(" | "),
          value: row.patientName,
          row,
        })),
    [admissionRows]
  );

  const loadRecordsForPatient = useCallback(
    async (admissionId) => {
      if (!admissionId) return false;
      const result = await dispatch(fetchIntakeOutputList(admissionId));
      if (fetchIntakeOutputList.rejected.match(result)) {
        showErrorToast(result.payload || "Failed to load Intake & Output records");
        return false;
      }
      return true;
    },
    [dispatch]
  );

  const handleSelectPatient = (row) => {
    if (!row) {
      dispatch(setPatient(null));
      setValue("patientSearch", null);
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    dispatch(setPatient(mapped));
    if (mapped.doctorId && mapped.doctorName) {
      dispatch(
        upsertDoctor({
          doctorId: mapped.doctorId,
          doctorName: mapped.doctorName,
        })
      );
    }
    setValue("patientSearch", {
      id: mapped.admissionId || mapped.uhid,
      label: mapped.patientName || "",
      value: mapped.contact || mapped.uhid || "",
    });
    if (mapped.doctorId) {
      const matchedDoctor =
        doctorOptions.find((d) => sameId(d.value, mapped.doctorId)) ||
        toDoctorOption(mapped.doctorId, mapped.doctorName);
      if (matchedDoctor) {
        setValue("doctor", matchedDoctor);
      }
    }
    dispatch(clearEditing());
    if (mapped.admissionId) {
      loadRecordsForPatient(mapped.admissionId);
    }
  };

  const handlePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectPatient(null);
      return;
    }
    if (selectedOption.row) {
      handleSelectPatient(selectedOption.row);
    }
  };

  const handleReset = () => {
    const { date, time } = getDefaultDateTime();
    reset({
      ...defaultValues,
      date,
      time,
      addedBy: authUser?.userName || "",
      patientSearch: patient
        ? {
            id: patient.admissionId || patient.uhid,
            label: patient.patientName,
            value: patient.contact || patient.uhid,
          }
        : null,
      doctor: patient?.doctorId
        ? toDoctorOption(patient.doctorId, patient.doctorName)
        : null,
    });
    dispatch(clearEditing());
  };

  const handleOpenList = async () => {
    if (!patient?.admissionId) {
      showWarningToast("Please select a patient first");
      return;
    }
    setListModalOpen(true);
    await loadRecordsForPatient(patient.admissionId);
  };

  const handleEditRow = async (row) => {
    if (!row?.inOutId || formBusy) return;
    const result = await dispatch(fetchIntakeOutputById(row.inOutId));
    if (fetchIntakeOutputById.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load Intake & Output record");
      return;
    }
    const detail = result.payload;
    const formValues = mapRecordToFormValues(detail);
    if (!formValues) return;

    if (detail.doctorId && detail.doctorName) {
      dispatch(
        upsertDoctor({
          doctorId: detail.doctorId,
          doctorName: detail.doctorName,
        })
      );
    }
    const matchedDoctor =
      toDoctorOption(detail.doctorId, detail.doctorName) ||
      doctorOptions.find((d) => sameId(d.value, detail.doctorId));

    setValue("date", formValues.date);
    setValue("time", formValues.time);
    setValue("doctor", matchedDoctor || null);
    setValue("addedBy", formValues.addedBy || authUser?.userName || "");
    setValue("oral", formValues.oral);
    setValue("ng", formValues.ng);
    setValue("intakeOthers", formValues.intakeOthers);
    setValue("iv", formValues.iv);
    setValue("totalParenteral", formValues.totalParenteral);
    setValue("intakeTotal", formValues.intakeTotal);
    setValue("urine", formValues.urine);
    setValue("drain", formValues.drain);
    setValue("aspiration", formValues.aspiration);
    setValue("outputOthers", formValues.outputOthers);
    setValue("outputTotal", formValues.outputTotal);
    setValue("remark", formValues.remark);
    setListModalOpen(false);
    showSuccessToast("Record loaded for edit");
  };

  const handleSave = handleSubmit(async (data) => {
    if (formBusy) return;
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }
    if (!patient.admissionId || !patient.regId) {
      showWarningToast("Selected patient is missing admission or registration id");
      return;
    }
    if (!data.doctor?.value && !data.doctor?.id) {
      showWarningToast("Please select a doctor");
      return;
    }

    const userId = Number(authUser?.userId || 1);
    const payload = mapFormToSavePayload(data, patient, userId, {
      isUpdate: Boolean(editingInOutId),
    });

    const result = await dispatch(
      saveIntakeOutput({
        inOutId: editingInOutId,
        payload,
      })
    );

    if (saveIntakeOutput.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to save Intake & Output chart");
      return;
    }

    showSuccessToast(
      editingInOutId
        ? "Intake & Output chart updated successfully"
        : "Intake & Output chart saved successfully"
    );
    handleReset();
    await loadRecordsForPatient(patient.admissionId);
  });

  const intakeOutputForm = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <CommonDatePicker
          name="date"
          control={control}
          label="Date"
          type="date"
          fontSize="text-xs"
          isFloatingLabel
        />
        <CommonTimePicker
          name="time"
          control={control}
          label="Time"
          fontSize="text-xs"
          isFloatingLabel
          use24Hour={false}
          format="hh:mm A"
        />
        <SearchDropdown
          name="doctor"
          control={control}
          label="Doctor"
          dataArray={doctorOptions}
          placeholder="Select"
          isSearchable
          isClearable
          fontSize="text-xs"
          isFloatingLabel
        />
        <InputField
          name="addedBy"
          control={control}
          label="Added By"
          fontSize="text-xs"
          isFloatingLabel
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded border border-gray-300 p-3 space-y-3">
          <CommonCheckbox
            name="intakeEnabled"
            control={control}
            label="Intake"
            fontSize="text-xs"
            checkboxSize="h-4 w-4"
          />
          <div className="grid grid-cols-2 gap-3">
            {INTAKE_FIELDS.map((field) => (
              <InputField
                key={field.name}
                name={field.name}
                control={control}
                label={field.label}
                fontSize="text-xs"
                isFloatingLabel
              />
            ))}
          </div>
        </div>

        <div className="rounded border border-gray-300 p-3 space-y-3">
          <CommonCheckbox
            name="outputEnabled"
            control={control}
            label="Output"
            fontSize="text-xs"
            checkboxSize="h-4 w-4"
          />
          <div className="grid grid-cols-2 gap-3">
            {OUTPUT_FIELDS.map((field) => (
              <InputField
                key={field.name}
                name={field.name}
                control={control}
                label={field.label}
                fontSize="text-xs"
                isFloatingLabel
              />
            ))}
          </div>
        </div>
      </div>

      <InputAreaField
        name="remark"
        control={control}
        label="Remark"
        rows={3}
        fontSize="text-xs"
        inputBgColor="bg-white"
        placeholder="Add remark (optional)"
      />
    </div>
  );

  const listSection = (
    <div className="rounded border border-gray-300">
      <div className="overflow-auto max-h-[60vh]">
        <table className="min-w-full text-[11px]">
          <thead className="bg-teal-700 text-white sticky top-0">
            <tr>
              {LIST_COLUMNS.map((col) => (
                <th key={col.key} className="px-2 py-1.5 text-left font-semibold">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  Loading records…
                </td>
              </tr>
            ) : tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  No intake &amp; output records.
                </td>
              </tr>
            ) : (
              tableRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-gray-200 hover:bg-teal-50 cursor-pointer"
                  onClick={() => handleEditRow(row)}
                >
                  {LIST_COLUMNS.map((col) => (
                    <td key={col.key} className="px-2 py-1.5 text-gray-700">
                      {row[col.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-2 sm:p-3 pb-24">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="sticky top-0 z-10 rounded-t-lg px-3 py-2 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90]">
            Intake &amp; Output Chart
          </h2>
        </div>

        <div className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div className="w-full sm:w-[40%]">
              <SearchDropdown
                name="patientSearch"
                control={control}
                label="Search by patient name or mobile number"
                dataArray={patientSearchOptions}
                placeholder="Search by patient name or UHID"
                isSearchable
                isClearable
                fontSize="text-xs sm:text-sm"
                isFloatingLabel
                onChange={handlePatientSearchSelect}
              />
            </div>
            <div className="flex-shrink-0">
              <CommonButton
                label="Admitted Patients"
                type="button"
                size="sm"
                className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
                onClick={() => setPatientsModalOpen(true)}
                disabled={patientsLoading}
              />
            </div>
          </div>
          <IpdPatientInfoCard patient={patient} />
          {patient && intakeOutputForm}
        </div>
      </div>

      <AdmittedPatientsModal
        isOpen={patientsModalOpen}
        onClose={() => setPatientsModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        onRowDoubleClick={handleSelectPatient}
        rows={admissionRows}
      />

      <CommonModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title="Intake & Output List"
        size="lg"
        maxHeightVh={90}
        paperSx={{
          width: { xs: "calc(100vw - 16px)", lg: "min(90vw, 960px)" },
          maxWidth: { lg: "min(90vw, 960px)" },
        }}
        disableBackdropClose={false}
        showCloseButton
        actions={[]}
      >
        {listSection}
      </CommonModal>

      <div className="fixed bottom-4 right-4 z-50 flex flex-wrap justify-end gap-2 sm:gap-3 pr-2 sm:pr-6">
        <CommonButton
          label="Intake & Output List"
          onClick={handleOpenList}
          variant="reset-outlined"
          size="sm"
          type="button"
          className="px-3 md:px-5 text-xs md:text-sm rounded-full bg-emerald-50 text-emerald-700 border border-emerald-400 hover:bg-emerald-100"
        />
        <CommonButton
          label="Reset"
          onClick={handleReset}
          variant="danger-outline"
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label={saving ? "Saving…" : editingInOutId ? "Update" : "Save"}
          onClick={handleSave}
          variant="primary-filled"
          size="sm"
          type="button"
          disabled={formBusy}
        />
      </div>
    </div>
  );
}

export default IntakeOutputChartScreen;
